import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  CircularProgress,
  useTheme,
} from "@mui/material";
import CustomTextField from "../../../../components/ui/Textfield";
import { Venue } from "../../../../types/venue";
import type { IThemeState } from "../../../../types/theme";
import { tokens } from "../../../../theme";

interface VenueDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  loading: boolean;
  newVenue: Venue;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  cities: string[];
  theme: IThemeState;
}

const VenueDialog: React.FC<VenueDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading,
  newVenue,
  onChange,
  cities,
  theme,
}) => {
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": {
        borderColor: theme.primary_colors?.[300] || "#ddd",
      },
      "&:hover fieldset": {
        borderColor: theme.neutral_colors?.[400] || "#bbb",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.primary_colors?.[400] || "#614438",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.primary_colors?.[300] || "#666",
    },
    "& .MuiInputBase-input": {
      color: theme.neutral_colors?.[100] || "#333",
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: colors.neutral[900],
          color: theme.neutral_colors?.[100] || "#333",
        }}
      >
        Add New Venue
      </DialogTitle>

      <DialogContent
        sx={{ backgroundColor: colors.neutral[900] }}
      >
        {[
          { label: "Venue Name", name: "name", required: true },
          { label: "Address One", name: "address_one", required: true },
          { label: "Address Two", name: "address_two", required: false },
          { label: "Suburb", name: "suburb", required: true },
        ].map(({ label, name, required }) => (
          <CustomTextField
            key={name}
            fullWidth
            label={label}
            name={name}
            value={String(newVenue[name as keyof Venue] ?? "")}
            onChange={onChange}
            variant="outlined"
            margin="normal"
            required={required}
            sx={textFieldStyles}
          />
        ))}

        <CustomTextField
          fullWidth
          label="City"
          name="city"
          value={newVenue.city}
          onChange={onChange}
          variant="outlined"
          margin="normal"
          select
          required
          sx={textFieldStyles}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {cities.map((city) => (
            <MenuItem key={city} value={city}>
              {city}
            </MenuItem>
          ))}
        </CustomTextField>
      </DialogContent>

      <DialogActions
        sx={{ backgroundColor: colors.neutral[900] }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: theme.primary_colors?.[100] || "#614438",
            borderColor: theme.primary_colors?.[100] || "#ddd",
            backgroundColor: theme.primary_colors?.[800],
            "&:hover": {
              borderColor: theme.neutral_colors?.[400] || "#bbb",
              backgroundColor: theme.neutral_colors?.[800] || "#f9f9f9",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={
            loading || !newVenue.name || !newVenue.address_one || !newVenue.city
          }
          sx={{
            backgroundColor: theme.primary_colors?.[400] || "#614438",
            color: theme.primary_colors?.[900] || "#fff",
            "&:hover": {
              backgroundColor: theme.primary_colors?.[500] || "#503629",
            },
            "&.Mui-disabled": {
              backgroundColor: theme.neutral_colors?.[400] || "#ddd",
              color: theme.primary_colors?.[900] || "#666",
            },
          }}
        >
          {loading ? (
            <CircularProgress
              size={20}
              sx={{ color: theme.neutral_colors?.[100] || "#fff" }}
            />
          ) : (
            "Add Venue"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VenueDialog;
